import { useEffect, useMemo, useState } from 'react';

import { useLazyQuery } from '@apollo/client';

import { SEARCH_WORKERS } from 'schemas';
import { IWorker } from 'types';
import { debounce } from 'utils';

interface IReturnValue {
  inputValue: string;
  setInputValue: (arg: string) => void;
  workers: Array<IWorker> | [];
}

export function useWorkers(
  value: Omit<IWorker, 'creator'> | null
): IReturnValue {
  const initialValue = useMemo(() => {
    const { last_name, first_name } = value || {};

    if (last_name && first_name) {
      return `${last_name} ${first_name}`;
    }
    return '';
  }, []);

  const [inputValue, setInputValue] = useState(initialValue);
  const [workers, setWorkers] = useState<IWorker[]>([]);

  const [getWorkers, { data }] = useLazyQuery(SEARCH_WORKERS, {
    fetchPolicy: 'no-cache',
  });

  const debouncedGetWorkers = useMemo(
    () => debounce(getWorkers, 300),
    [getWorkers]
  );

  useEffect(() => {
    if (inputValue.length > 1) {
      debouncedGetWorkers({ variables: { input: { q: inputValue } } });
    } else {
      setWorkers([]);
    }
  }, [debouncedGetWorkers, inputValue, setWorkers]);

  useEffect(() => {
    if (data) {
      setWorkers(data.workers.data);
    }
  }, [data]);

  return {
    inputValue,
    setInputValue,
    workers,
  };
}
